import type { RecognitionErrorEnvelope } from "@/api/types";

/**
 * Typed wrapper for non-2xx responses from the backend. Carries the
 * `RecognitionError` envelope verbatim so callers can pattern-match `code`
 * without re-parsing the response body.
 *
 * Synthetic `code: "network_error"` (status 0) is used when `fetch` itself
 * rejects (offline, DNS failure, CORS) — see `apiFetch`. The original
 * exception is preserved via `cause` so devtools/Sentry can drill down.
 */
export interface ApiErrorInit {
  code: string;
  message: string;
  status: number;
  details?: Record<string, unknown>;
  cause?: unknown;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown>;

  constructor(
    arg1: ApiErrorInit | number,
    envelope?: RecognitionErrorEnvelope,
  ) {
    if (typeof arg1 === "number") {
      const env = envelope ?? {
        code: "unknown_error",
        message: `HTTP ${arg1}`,
      };
      super(env.message || `HTTP ${arg1}`);
      this.code = env.code || "unknown_error";
      this.status = arg1;
      this.details = env.details ?? {};
    } else {
      super(arg1.message || `HTTP ${arg1.status}`);
      this.code = arg1.code || "unknown_error";
      this.status = arg1.status;
      this.details = arg1.details ?? {};
      if (arg1.cause !== undefined) {
        (this as { cause?: unknown }).cause = arg1.cause;
      }
    }
    this.name = "ApiError";
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Render an unknown error into a user-presentable string.
 *
 * Priority:
 *   1. `ApiError` → use the server-supplied `message` (RecognitionError
 *      envelope) so the operator sees the real cause (e.g. "missing field
 *      email", "invite already exists"), not a generic stub.
 *   2. Plain `Error` → its `.message`.
 *   3. Strings → as-is.
 *   4. Anything else → optional fallback (caller-provided), else "".
 *
 * Empty strings are filtered so a server returning ``{message: ""}`` does
 * not produce a blank UI alert — fallback wins in that case.
 */
export function toMessage(error: unknown, fallback?: string): string {
  if (isApiError(error)) {
    const msg = error.message?.trim();
    if (msg && msg.length > 0) return msg;
  }
  if (error instanceof Error) {
    const msg = error.message?.trim();
    if (msg && msg.length > 0) return msg;
  }
  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }
  return fallback ?? "";
}
